/**
 * MapPage — /map. Full interactive "explore stories by place" page.
 * Countries with stories glow; clicking filters the home gallery.
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Film, Loader2, MapPin, X, ArrowRight } from "lucide-react";
import { useTranslation } from "@/i18n";
import LanguagePicker from "@/LanguagePicker";
import WorldMap from "@/WorldMap";
import { mediaUrl } from "@/mediaUrl";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MapPage() {
  const [t] = useTranslation();
  const [countries, setCountries] = useState(null);
  const [sel, setSel] = useState(null); // selected country name
  const [selStories, setSelStories] = useState(null);
  const regionRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/stories/facets`)
      .then(({ data }) => setCountries(data.countries || []))
      .catch(() => setCountries([]));
  }, []);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setSel(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const openCountry = (name) => {
    setSel(name);
    setSelStories(null);
    axios.get(`${API}/stories`, { params: { country: name } })
      .then(({ data }) => setSelStories(data || []))
      .catch(() => setSelStories([]));
  };

  return (
    <div className="min-h-screen" data-testid="map-page">
      <header className="px-6 md:px-10 py-6 md:py-8 border-b border-white/5">
        <div className="max-w-[1200px] mx-auto flex items-center justify-between">
          <a href="/" className="flex items-center gap-3">
            <img src="/brand-s.png" alt="Storylo" className="w-9 h-9 rounded-full" data-testid="brand-logo" />
            <div>
              <div className="font-display text-lg leading-none">Storylo</div>
              <div className="text-[10px] tracking-[0.22em] uppercase text-[color:var(--muted)] mt-1">
                {t("tagline")}
              </div>
            </div>
          </a>
          <nav className="flex items-center gap-3 sm:gap-4 text-sm">
            <a href="/" className="text-[color:var(--muted)] hover:text-white transition-colors">{t("nav_all")}</a>
            <a href="/map" className="text-white">{t("nav_map")}</a>
            <a href="/impact" className="text-[color:var(--muted)] hover:text-white transition-colors">{t("nav_impact")}</a>
            <LanguagePicker />
          </nav>
        </div>
      </header>

      <main className="px-6 md:px-10 py-12 md:py-16">
        <div className="max-w-[1200px] mx-auto">
          <div className="text-[10px] tracking-[0.22em] uppercase text-[color:var(--accent)] mb-3 flex items-center gap-2">
            <MapPin className="w-3.5 h-3.5" /> {t("home_map_eyebrow")}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
            {t("map_title")}
          </h1>
          <p className="text-[color:var(--muted)] max-w-2xl mt-4 leading-relaxed text-base sm:text-lg">
            {t("map_sub")}
          </p>

          {countries === null ? (
            <div className="flex items-center justify-center py-24">
              <Loader2 className="w-8 h-8 animate-spin text-[color:var(--accent)]" />
            </div>
          ) : (
            <>
              <div className="mt-10 panel p-4 md:p-6">
                <WorldMap countries={countries} onSelect={openCountry} />
              </div>

              {/* Inline region gallery — clicking a country opens ALL its
                  stories right below the map. */}
              {sel && (
                <div ref={regionRef} className="mt-8 scroll-mt-24 panel p-6 md:p-8" data-testid="map-region-section">
                  <div className="flex items-start justify-between gap-4 mb-6">
                    <div>
                      <div className="text-[10px] tracking-[0.22em] uppercase text-[color:var(--accent)] mb-2 flex items-center gap-1.5">
                        <MapPin className="w-3 h-3" /> {t("home_map_eyebrow")}
                      </div>
                      <h2 className="font-display text-2xl md:text-3xl tracking-tight">{sel}</h2>
                      {selStories && (
                        <div className="text-xs text-[color:var(--muted)] mt-1">
                          {selStories.length} {selStories.length === 1 ? "story" : "stories"}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => setSel(null)}
                      className="p-2 rounded-lg border border-white/10 text-[color:var(--muted)] hover:text-white hover:border-white/30"
                      data-testid="map-region-close"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>

                  {selStories === null ? (
                    <div className="flex items-center justify-center py-14">
                      <Loader2 className="w-6 h-6 animate-spin text-[color:var(--accent)]" />
                    </div>
                  ) : selStories.length === 0 ? (
                    <div className="py-10 text-center text-sm text-[color:var(--muted)]">
                      No published stories from {sel} yet.
                    </div>
                  ) : (
                    <>
                      <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="map-region-stories">
                        {selStories.map((s) => (
                          <a
                            key={s.slug}
                            href={`/s/${s.slug}`}
                            className="panel overflow-hidden group block"
                            data-testid={`map-region-story-${s.slug}`}
                          >
                            <div className="aspect-video bg-white/[0.04] overflow-hidden">
                              {s.thumbnail_url ? (
                                <img
                                  src={mediaUrl(s.thumbnail_url)}
                                  alt={s.title}
                                  loading="lazy"
                                  className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-300"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <Film className="w-7 h-7 text-[color:var(--muted)] opacity-40" />
                                </div>
                              )}
                            </div>
                            <div className="p-3">
                              <div className="text-sm font-display leading-snug line-clamp-2 group-hover:text-[color:var(--accent)] transition-colors">
                                {s.title}
                              </div>
                              <div className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--muted)] mt-1.5">
                                {new Date(s.created_at).toLocaleDateString(undefined, { year: "numeric", month: "short" })}
                              </div>
                            </div>
                          </a>
                        ))}
                      </div>
                      <a
                        href={`/?country=${encodeURIComponent(sel)}`}
                        className="btn-primary text-sm inline-flex items-center gap-2 px-5 py-2.5 mt-6"
                        data-testid="map-region-see-all"
                      >
                        {t("map_see_all", { count: selStories.length, place: sel })}
                        <ArrowRight className="w-4 h-4" />
                      </a>
                    </>
                  )}
                </div>
              )}

              {countries.length > 0 && (
                <div className="mt-8">
                  <div className="text-[10px] tracking-[0.22em] uppercase text-[color:var(--muted)] mb-4">
                    {t("map_all_places")}
                  </div>
                  <div className="flex flex-wrap gap-2" data-testid="map-country-chips">
                    {countries.map((c) => (
                      <button
                        key={c.name}
                        onClick={() => openCountry(c.name)}
                        className="text-sm px-3.5 py-1.5 rounded-full border border-white/15 hover:border-[color:var(--accent)] hover:text-[color:var(--accent)] transition-colors inline-flex items-center gap-2"
                        data-testid={`map-chip-${c.name}`}
                      >
                        {c.name}
                        <span className="text-[10px] text-[color:var(--muted)]">{c.count}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {countries.length === 0 && (
                <div className="mt-10 panel p-10 text-center text-[color:var(--muted)]">
                  No stories have a place set yet — check back soon.
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}
